import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Avatar, Button } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { format } from 'date-fns';
import { MilesMessage, WidgetConfig } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MilesCarousel, { Vehicle } from './MilesCarousel';
import { useMemo } from 'react';
// import slugify from 'slugify';
import WidgetAPI from '../services/api';
import { getAssetURL, getUuidFromUrl } from '../utils/url';
import { toast } from 'react-toastify';
import { IsNotNullOrEmpty } from '../utils/validation';

interface MessageProps {
  message: MilesMessage;
  iconUrl: string;
  webUrl: string;
  config: WidgetConfig;
  onAction?: (action: string) => void;
  onEnhanceClick: (currentVehicle?: Vehicle) => void;
  onVehicleClick: (currentVehicle?: Vehicle) => void;
  onContentHeightChange?: () => void;
}

const Message: React.FC<MessageProps> = ({ message, iconUrl, webUrl, config, onAction, onEnhanceClick, onVehicleClick, onContentHeightChange }) => {
  const [api] = useState(() => new WidgetAPI(config));
  const isUser = message.senderType === 'user';
  const isMiles = message.senderType === 'miles';

  const [carouselVehicles, setCarouselVehicles] = useState<any[]>([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [isVehicleLoading, setIsVehicleLoading] = useState(false);
  const [queryParams, setQueryParams] = useState<any>({});

  // Check if message has action buttons (e.g., TCPA consent)
  const hasActions = message.metadata?.actions && Array.isArray(message.metadata.actions);

  const updatedVehicles = useMemo(() => {
    if (message.messageType !== 'carousel') return [];
    const raw = (message.metadata as any)?.properties || (message.metadata as any)?.vehicles || (message.metadata as any)?.cars || [];
    setTotalVehicles((message.metadata as any)?.total || 0);
    setQueryParams((message.metadata as any)?.queryParams || {});

    if (!Array.isArray(raw)) return [];
    return raw
      .map((item: any) => ({
        id: item?.id ?? item?._id ?? '',
        title: item?.title ?? item?.unparsedAddress ?? item?.name,
        brand: item?.city ?? item?.brand ?? item?.carBrand,
        model: item?.state ?? item?.model ?? item?.carModel,
        price: item?.price ?? item?.listPrice,
        mileage: item?.livingArea ?? item?.area ?? item?.mileage,
        exterior: item?.propertyType ?? item?.type ?? item?.exteriorColor ?? item?.color,
        vinNumber: item?.listingId ?? item?.mlsNumber ?? item?.vinNumber,
        // imageUrl: item?.imageUrl ?? item?.image,
        imageUrl: getAssetURL(config.assetsUrl, item?.imageKey, item?.imageUrl ?? item?.image),
        carUrl: item?.link ?? item?.carUrl,
      }))
      .filter((item) => item.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.messageType, message.metadata]);

  useEffect(() => {
    // Whenever updatedVehicles change, update the state
    setCarouselVehicles(updatedVehicles);
  }, [updatedVehicles]);

  useEffect(() => {
    if (carouselVehicles.length > 0 && typeof onContentHeightChange === 'function') {
      onContentHeightChange();
    }
  }, [carouselVehicles, onContentHeightChange]);

  const loadMoreVehicles = async () => {
    try {
      // Prevent duplicate API calls while already loading
      if (isVehicleLoading) return;

      if (carouselVehicles.length >= totalVehicles) return;

      setIsVehicleLoading(true);
      const raw = (await api.getProperties({ ...queryParams, skip: carouselVehicles.length }))?.data;

      if (!Array.isArray(raw)) return;

      const mappedVehicles = raw
        .map((item: any) => ({
          id: item?.id ?? item?._id ?? '',
          title: item?.title ?? item?.unparsedAddress ?? item?.name,
          brand: item?.city ?? item?.car_brand?.title ?? item?.brand ?? item?.carBrand,
          model: item?.state ?? item?.car_model?.title ?? item?.model ?? item?.carModel,
          price: item?.price ?? item?.listPrice,
          mileage: item?.livingArea ?? item?.area ?? item?.mileage,
          exterior: item?.propertyType ?? item?.type ?? item?.exterior ?? item?.exteriorColor ?? item?.color,
          vinNumber: item?.listingId ?? item?.mlsNumber ?? item?.vinNumber,
          imageUrl: getAssetURL(
            config.assetsUrl,
            item?.photos?.[0]?.key ?? item?.imageKey,
            item?.imageUrl ?? item?.image
          ),
          carUrl: item?.link ?? item?.carUrl,
        }))
        .filter((item) => item.id);

      setCarouselVehicles((prev) => [...prev, ...mappedVehicles]);

    } catch (err) { }
    finally {
      setIsVehicleLoading(false);
    }
  }

  const sanitizeMarkdown = () => (tree: any) => {
    const prune = (node: any): boolean => {
      if (node.type === 'image') return false;

      if (node.children) {
        node.children = node.children.filter(prune);
      }

      if (node.type === 'text') {
        return Boolean(node.value && node.value.trim());
      }

      if (node.children && node.children.length === 0) {
        return false;
      }

      if (node.type === 'listItem') {
        return Array.isArray(node.children) && node.children.length > 0;
      }

      return true;
    };

    if (tree.children) {
      tree.children = tree.children.filter(prune);
    }
  };

  const handleVehicleClick = (id?: string, carUrl?: string, selectedVehicle?: any) => {
    if (!id) return;
    // const vehicle = carouselVehicles.find((item) => item.id === id);
    // const vehicleTitle =
    //   vehicle?.title || `${vehicle?.brand ?? ''} ${vehicle?.model ?? ''}`.trim() || 'vehicle';


    // const slugTitle = slugify(vehicleTitle, {
    //   lower: true,
    //   strict: true, // removes / and other symbols
    // });

    // Track the property scan and update SAGE context.
    try {
      if (carUrl) {
        api.scanProperty(carUrl);
        api.handleLikeProperty(id);
      }
    } catch (err) { }


    // const baseUrl = (webUrl || window.location.origin).replace(/\/$/, '');
    // const targetUrl = carUrl || `${baseUrl}/car-info/${slugTitle}/${id}`;
    // window.open(targetUrl, '_blank', 'noopener');
    onVehicleClick(selectedVehicle);
  };

  const onLikeVehicleClick = (id: string, carUrl?: string) => {
    try {
      if (id) api.handleLikeProperty(id);
      // if (carUrl) api.scanProperty(carUrl);
    } catch (err) { }
  }

  const handleCopyLink = (shareLink?: string) => {
    if (!shareLink) {
      toast.error('No link to copy.');
      return;
    }

    if (!navigator.clipboard?.writeText) {
      toast.error('Copy not supported in this browser.');
      return;
    }

    navigator.clipboard
      .writeText(shareLink)
      .then(() => toast.success('Link copied to clipboard!'))
      .catch(() => toast.error('Failed to copy link.'));
  };


  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          gap: 1,
          mb: 2,
          px: 2,
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: 36,
            height: 36,
            // background: isMiles ? 'linear-gradient(to right, #004aad, #e385ec)' : '#C4CDD5',
            background: isMiles ? 'linear-gradient(338.5deg, #7699F7 0.63%, #3C69EA 100%)' : '#C4CDD5',
            flexShrink: 0,
            p: isMiles ? 0.3 : 0,
          }}
          src={isMiles ? iconUrl : undefined}
        >
          {!isMiles && <PersonIcon fontSize="small" />}
        </Avatar>

        {/* Message Bubble */}
        <Box sx={{ maxWidth: '70%', minWidth: '100px' }}>
          <Box
            sx={{
              p: '1px',
              borderRadius: 1,
              // background: 'linear-gradient(to right, #8c52ff, #5ce1e6)',
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                // backgroundColor: isUser ? '#fff' : '#8669fa',
                backgroundColor: isUser ? '#fff' : '#fff',
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  fontSize: '0.9rem',
                  wordBreak: 'break-word',
                  // color: isUser ? '#000' : '#fff',
                  color: isUser ? 'text.primary' : 'text.primary',
                  '& p': {
                    margin: 0,
                    marginBottom: '0.5em',
                    '&:last-child': {
                      marginBottom: 0,
                    },
                  },
                  '& strong': {
                    fontWeight: 600,
                  },
                  '& ul, & ol': {
                    marginTop: '0.5em',
                    marginBottom: '0.5em',
                    paddingLeft: '1.5em',
                  },
                  '& li': {
                    marginBottom: '0.25em',
                  },
                  '& a': {
                    color: '#1976d2',
                    fontWeight: 500,
                    textDecoration: 'underline',
                    wordBreak: 'break-all',
                    '&:hover': {
                      color: '#1565c0',
                    },
                  },
                }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography variant="body2" component="p">
                        {children}
                      </Typography>
                    ),
                    strong: ({ children }) => <strong>{children}</strong>,
                    a: ({ href, children }) => (
                      // <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'text.primary', fontWeight: 'bold' }}>
                      //   {children}
                      // </a>
                      <Box component={'a'}
                        sx={{ color: 'text.primary', fontWeight: 'bold', cursor: 'pointer' }}
                        onClick={() => {
                          if (href?.includes('/property-info/') || href?.includes('/car-info/')) {
                            const carId = getUuidFromUrl(href);

                            if (IsNotNullOrEmpty(carId)) {
                              const carData = carouselVehicles.find((item) => item.id === carId);

                              if (IsNotNullOrEmpty(carData)) {
                                onVehicleClick(carData);
                                return;
                              }
                            }
                          }
                          window.open(href, '_blank', 'noopener,noreferrer');
                        }}
                      >{children}</Box>
                    ),
                    img: () => null,
                  }}
                  remarkPlugins={[remarkGfm, sanitizeMarkdown]}
                >
                  {message.message}
                </ReactMarkdown>





                {message?.metadata?.messageType === "action" && message?.metadata?.showCopyLink === true && message?.metadata?.shareLink && (
                  <Button
                    key={'copy-car-link'}
                    variant={'contained'}
                    size="small"
                    onClick={() => handleCopyLink(message?.metadata?.shareLink)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      width: "100%",
                      fontWeight: 600,
                      ...{
                        background: '#ffffff',
                        color: '#666666',
                        '&:hover': {
                          background: '#ffffff',
                        },
                      },
                    }}
                  >
                    Copy Link
                  </Button>
                )}


              </Box>
            </Paper>
          </Box>

          {/* Timestamp */}
          <Typography
            variant="caption"
            color="white"
            sx={{
              display: 'block',
              mt: 0.5,
              px: 0.5,
              textAlign: isUser ? 'right' : 'left',
            }}
          >
            {format(new Date(message.createdAt), 'h:mm a')}
          </Typography>

          {/* Action Buttons (e.g., TCPA consent) */}
          {hasActions && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 1,
                flexDirection: isUser ? 'row-reverse' : 'row',
              }}
            >
              {message.metadata.actions.map((action: any, index: number) => (
                <Button
                  key={index}
                  variant={action.action === 'tcpa_agree' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => onAction?.(action.action)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    ...(action.action === 'tcpa_agree' && {
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      color: '#fff',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                      },
                    }),
                    ...(action.action === 'tcpa_cancel' && {
                      borderColor: '#999',
                      color: '#666',
                      '&:hover': {
                        borderColor: '#666',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }),
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      {carouselVehicles.length > 0 && (
        <Box sx={{ m: 1.5 }}>
          <MilesCarousel vehicles={carouselVehicles} totalVehicles={totalVehicles} isVehicleLoading={isVehicleLoading} onLoadMoreVehicles={loadMoreVehicles} onVehicleClick={handleVehicleClick} onLikeVehicleClick={onLikeVehicleClick} onEnhanceClick={onEnhanceClick} config={config} />
        </Box>
      )}
    </>
  );
};

export default Message;
